// @flow
import invariant from 'invariant';
import { truncateToUTF8Bytes } from 'utils/string';
import {
  arrToObject,
  updateObject,
  isDefined,
  filterObject,
  difference,
  removeKeys
} from 'utils/functional';
import { levelOptions, levelOptionTitles } from 'utils/search/sidefilters';
import {
  ACTION_TYPE_PLURAL,
  AREAS_OF_STUDY,
  EDUCATION_LEVELS,
  PAYMENT_LEVELS,
  JOB_FUNCTIONS,
  INTERNSHIP_FUNCTIONS,
  VOLOP_FUNCTIONS,
  AREAS_OF_FOCUS,
  VOLOP_SOURCES,
  LOCALES,
  ORG_TYPES,
  ORG_TYPES_ALLOW_TO_POST_VOLOPS,
  PROFESSIONAL_LEVELS,
  VOLOP_SOURCE_FOR_ACTION_TYPE
} from 'utils/constants';
import {
  initialFacetFiltersForMainSearch,
  sideFacetsForType
} from 'utils/search/searchFacets';
import * as localStorage from 'utils/localStorage';
import type { Action } from '../actions/mainSearch';
import { USE_LOCATION_LOCAL_STORAGE_KEY } from '../actions/mainSearch';

const orgTypeFacet: SearchFacet = {
  id: 'orgTypeFacet',
  name: 'orgType',
  title: getText('Org Type'),
  type: 'array',
  options: levelOptions(ORG_TYPES),
  optionTitles: levelOptionTitles(ORG_TYPES)
};

const searchFacets: { [string]: SearchFacet } = {
  remoteOkFacet: {
    id: 'remoteOkFacet',
    name: 'remoteOk',
    title: getText('Remote / On-Site'),
    type: 'boolean',
    options: ['YES', 'NO'],
    optionTitles: {
      YES: getText('Remote'),
      NO: getText('On Site')
    }
  },
  employmentTypeFacet: {
    id: 'employmentTypeFacet',
    name: 'isFullTime',
    title: getText('Full Time / Part Time'),
    type: 'boolean',
    options: ['YES', 'NO'],
    optionTitles: {
      YES: getText('Full Time'),
      NO: getText('Part Time')
    }
  },
  isVirtualFacet: {
    id: 'isVirtualFacet',
    name: 'isVirtual',
    title: getText('Virtual'),
    type: 'boolean',
    options: ['YES', 'NO'],
    optionTitles: {
      YES: getText('Virtual'),
      NO: getText('Not Virtual')
    }
  },
  isCovidFacet: {
    id: 'isCovidFacet',
    name: 'isCovid',
    title: getText('Covid'),
    type: 'boolean',
    options: ['YES', 'NO'],
    optionTitles: {
      YES: getText('COVID-19'),
      NO: getText('Not COVID-19')
    }
  },
  hasLocationFacet: {
    id: 'hasLocationFacet',
    name: 'hasLocation',
    title: getText('Has Location'),
    type: 'boolean',
    options: ['YES', 'NO'],
    optionTitles: {
      YES: getText('Has Location'),
      NO: getText('No Location')
    }
  },
  paidFacet: {
    id: 'paidFacet',
    name: 'paid',
    title: getText('Is paid'),
    type: 'boolean',
    options: ['YES', 'NO'],
    optionTitles: {
      YES: getText('Paid'),
      NO: getText('Unpaid')
    }
  },
  paymentFacet: {
    id: 'paymentFacet',
    name: 'payment',
    title: getText('Compensation'),
    type: 'array',
    options: levelOptions(PAYMENT_LEVELS),
    optionTitles: levelOptionTitles(PAYMENT_LEVELS)
  },
  educationFacet: {
    id: 'educationFacet',
    name: 'education',
    title: getText('Education Level'),
    type: 'array',
    options: levelOptions(EDUCATION_LEVELS),
    optionTitles: levelOptionTitles(EDUCATION_LEVELS)
  },
  actionTypeFacet: {
    id: 'actionTypeFacet',
    name: 'actionType',
    title: getText('Opportunity Type'),
    type: 'array',
    options: difference(levelOptions(ACTION_TYPE_PLURAL), ['NONE', 'OTHER']),
    optionTitles: removeKeys(levelOptionTitles(ACTION_TYPE_PLURAL), [
      'NONE',
      'OTHER'
    ]),
    facetToResetOnChange: 'volopSourceFacet',
    includeAdditionalOption: { VOLOP: 'OTHER' }
  },
  professionalLevelFacet: {
    id: 'professionalLevelFacet',
    name: 'professionalLevel',
    title: getText('Professional Level'),
    type: 'array',
    options: levelOptions(PROFESSIONAL_LEVELS),
    optionTitles: levelOptionTitles(PROFESSIONAL_LEVELS)
  },
  localeFacet: {
    id: 'localeFacet',
    name: 'locale',
    title: getText('Listing Language'),
    type: 'array',
    options: levelOptions(LOCALES),
    optionTitles: levelOptionTitles(LOCALES)
  },
  actionDateFacet: {
    id: 'actionDateFacet',
    name: 'actionDate',
    names: ['starts', 'ends'],
    title: getText('Date'),
    type: 'date',
    format: 'semantic',
    // Do not apply to date filters
    options: [],
    optionTitles: {}
  },
  orgTypeFacet,
  volopOrgTypeFacet: {
    ...orgTypeFacet,
    id: 'volopOrgTypeFacet',
    options: levelOptions(ORG_TYPES_ALLOW_TO_POST_VOLOPS),
    optionTitles: levelOptionTitles(ORG_TYPES_ALLOW_TO_POST_VOLOPS)
  },
  jobKeywordsFacet: {
    id: 'jobKeywordsFacet',
    name: 'functions',
    title: getText('Job Function'),
    type: 'array',
    options: Object.keys(JOB_FUNCTIONS),
    optionTitles: JOB_FUNCTIONS
  },
  internshipKeywordsFacet: {
    id: 'internshipKeywordsFacet',
    name: 'functions',
    title: getText('Functions'),
    type: 'array',
    options: Object.keys(INTERNSHIP_FUNCTIONS),
    optionTitles: INTERNSHIP_FUNCTIONS
  },
  volopKeywordsFacet: {
    id: 'volopKeywordsFacet',
    name: 'functions',
    title: getText('Skills'),
    type: 'array',
    options: Object.keys(VOLOP_FUNCTIONS),
    optionTitles: VOLOP_FUNCTIONS
  },
  orgAreasOfFocusFacet: {
    id: 'orgAreasOfFocusFacet',
    name: 'areasOfFocus',
    title: getText('Issue Areas'),
    type: 'array',
    options: Object.keys(AREAS_OF_FOCUS),
    optionTitles: AREAS_OF_FOCUS
  },
  volopAreasOfFocusFacet: {
    id: 'volopAreasOfFocusFacet',
    name: 'areasOfFocus',
    title: getText('Issue Areas'),
    type: 'array',
    options: Object.keys(AREAS_OF_FOCUS),
    optionTitles: AREAS_OF_FOCUS
  },
  volopSourceFacet: {
    id: 'volopSourceFacet',
    name: 'source',
    title: getText('Source'),
    type: 'array',
    options: Object.keys(VOLOP_SOURCES),
    optionTitles: VOLOP_SOURCES,
    optionCullFn: filters => {
      const allFiltersSelected =
        filters.actionTypeFacet instanceof Array &&
        filters.actionTypeFacet.length ===
          searchFacets.actionTypeFacet.options.length;
      if (!allFiltersSelected) {
        const sources = new Set();
        filters.actionTypeFacet.forEach(facet => {
          VOLOP_SOURCE_FOR_ACTION_TYPE[facet].forEach(source => {
            sources.add(source);
          });
        });
        return Array.from(sources).filter(source => source !== 'NONE');
      }
      return Object.keys(VOLOP_SOURCES);
    }
  },
  idealistDayEventAreasOfFocusFacet: {
    id: 'idealistDayEventAreasOfFocusFacet',
    name: 'areasOfFocus',
    title: getText('Issue Areas'),
    type: 'array',
    options: Object.keys(AREAS_OF_FOCUS),
    optionTitles: AREAS_OF_FOCUS
  },
  groupAreasOfFocusFacet: {
    id: 'groupAreasOfFocusFacet',
    name: 'areasOfFocus',
    title: getText('Issue Areas'),
    type: 'array',
    options: Object.keys(AREAS_OF_FOCUS),
    optionTitles: AREAS_OF_FOCUS
  },
  publicPrivateFacet: {
    id: 'publicPrivateFacet',
    name: 'isPublicSchool',
    title: getText('Public / Private'),
    type: 'boolean',
    options: ['YES', 'NO'],
    optionTitles: {
      YES: getText('Public'),
      NO: getText('Private')
    }
  },
  gradAreasOfStudyFacet: {
    id: 'gradAreasOfStudyFacet',
    name: 'areasOfStudy',
    title: getText('Areas of Study'),
    type: 'array',
    options: Object.keys(AREAS_OF_STUDY),
    optionTitles: AREAS_OF_STUDY
  },
  idealistDayFacet: {
    id: 'idealistDayFacet',
    sort: false,
    lazy: true,
    name: 'idealistDay',
    title: getText('Date'),
    type: 'array',
    options: [], // Lazy loaded
    optionTitles: {} // Lazy loaded
  }
};

const DEFAULT_TYPE_FACET = 'JOB';

const initialFacetsThatArentLoaded = Object.keys(
  filterObject(searchFacets, (_, f) => !!f.lazy)
);

const mainSearchTypes = [
  'JOB',
  'INTERNSHIP',
  'VOLOP',
  'ORG',
  'GROUP',
  'IDEALIST_DAY_EVENT',
  'GRAD_PROGRAM',
  'ALL'
];

const mainIndexTypes = [
  'JOB',
  'INTERNSHIP',
  'VOLOP',
  'ORG',
  'GROUP',
  'IDEALIST_DAY_EVENT',
  'ALL'
];

export const TYPE_FILTER_TYPES: Array<ListingType> = [
  'JOB',
  'INTERNSHIP',
  'VOLOP',
  'ORG',
  'GROUP',
  'IDEALIST_DAY_EVENT',
  'ALL'
];

export type SortType = 'relevance' | 'newest';

export type GradSortType =
  | 'program-name-asc'
  | 'program-name-desc'
  | 'financial-aid-asc'
  | 'financial-aid-desc'
  | 'nb-of-students-asc'
  | 'nb-of-students-desc'
  | 'acceptance-rate-asc'
  | 'acceptance-rate-desc';

export type ResultsViewType = 'list' | 'map';

export type MainSearchResults = SearchResults & {
  searchLocation: SearchLocation
};

export type State = {
  query: string,
  pageIndex: number,
  lastLoadedPageIndex: number,
  searchId: number,
  resultsByPage: { [string]: MainSearchResults },
  // true if the same query has results if filtered by org
  hasOrgResults?: boolean,
  resultsForSearchId?: ?string,
  initialized: boolean,
  landingPageInitialized: boolean,
  initialSearchStarted: boolean,
  initialResultsLoaded: boolean,
  isSearching: boolean,
  searchMode: boolean,
  sort: { [ListingType]: SortType | GradSortType },
  searchFacets: { [string]: SearchFacet },
  facetsThatArentLoaded: Array<string>,
  filters: SearchFiltersWithType<ListingType>,
  landingPagePath: ?string,
  queryID: ?string,
  autosaveSearch?: boolean,
  autosaveDisabled?: boolean,
  isStaff?: boolean,
  serverInitialized: boolean,
  useLocation: boolean
};

const initialState = {
  query: '',
  pageIndex: 0,
  lastLoadedPageIndex: 0,
  searchId: 0, // Incrementing id
  resultsByPage: {},
  initialized: false,
  landingPageInitialized: false,
  initialSearchStarted: false,
  initialResultsLoaded: false,
  isSearching: false,
  searchMode: false,
  sort: updateObject(
    { GRAD_PROGRAM: 'program-name-asc' },
    arrToObject(mainIndexTypes.map(t => [t, 'relevance']))
  ),
  searchFacets,
  // Dont render search results that filter based on these facets until this is
  // loaded
  facetsThatArentLoaded: initialFacetsThatArentLoaded,
  filters: {},
  landingPagePath: null,
  queryID: null,
  serverInitialized: false,
  useLocation: localStorage.getItem(USE_LOCATION_LOCAL_STORAGE_KEY) || false
};

export default function mainSearch(s: State = initialState, action: Action) {
  // Server initialized: did the server provide redux data?
  // If so, let it be combined with the client-defined initialState on the
  // first run of this reducer
  const state = s.serverInitialized
    ? { ...initialState, ...s, serverInitialized: false }
    : s;

  switch (action.type) {
    case 'SEARCH_INITIALIZE': {
      const { page, q, sort, type } = action.queryParams;
      const query = Array.isArray(q) ? q[0] : q;
      const defaultSearchType = action.defaultSearchType;
      const filters = initialFacetFiltersForMainSearch(
        // This should be handled in the searchFacet reducer
        // If there is new search facets we need to update initialSearchFacets
        state.searchFacets,
        state.facetsThatArentLoaded,
        {
          ...action.queryParams,
          type: type || defaultSearchType || DEFAULT_TYPE_FACET
        }
      );
      return {
        ...state,
        initialized: true,
        query: query ? truncateToUTF8Bytes(query || '', 512) : state.query,
        pageIndex: page ? Number(page) - 1 : state.pageIndex,
        sort: sort ? updateObject(state.sort, { [type]: sort }) : state.sort,
        filters
      };
    }

    case 'SEARCH_INITIALIZE_LANDING_PAGE': {
      const filters = initialFacetFiltersForMainSearch(
        // This should be handled in the searchFacet reducer
        // If there is new search facets we need to update initialSearchFacets
        state.searchFacets,
        state.facetsThatArentLoaded,
        {
          ...action.filters,
          type: (action.filters && action.filters.type) || DEFAULT_TYPE_FACET
        }
      );

      return {
        ...state,
        initialized: true,
        landingPageInitialized: true,
        landingPagePath: action.landingPagePath,
        filters,
        useLocation: action.useLocation || state.useLocation,
        autosaveSearch: action.autosaveSearch
      };
    }

    case 'SEARCH_RESET_PAGE': {
      return {
        ...state,
        searchId: state.searchId + 1,
        pageIndex: 0, // New search, reset page
        landingPagePath: null
      };
    }

    case 'SEARCH_SET_QUERY': {
      return {
        ...state,
        searchId: state.searchId + 1,
        pageIndex: 0, // New search, reset page
        landingPagePath: null,
        query: action.query,
        searchMode: true
      };
    }

    case 'SEARCH_SET_SORT': {
      return {
        ...state,
        searchId: state.searchId + 1,
        pageIndex: 0, // New search, reset page
        sort: updateObject(state.sort, {
          [state.filters.type]: action.sortParam
        }),
        searchMode: true
      };
    }

    case 'SEARCH_SET_PAGE_INDEX': {
      return {
        ...state,
        pageIndex: action.pageIndex,
        searchMode: true
      };
    }

    case 'SEARCH_SELECT_FILTER_OPTION': {
      const facet = action.key;
      const filter = state.filters[facet];
      const selectedOptions = [
        ...((Array.isArray(filter) && filter) || []),
        action.option
      ];

      return {
        ...state,
        searchId: state.searchId + 1,
        pageIndex: 0, // New search, reset page
        landingPagePath: null,
        filters: {
          ...state.filters,
          [facet]: selectedOptions
        }
      };
    }

    case 'SEARCH_RESET_FILTERS': {
      const filters = initialFacetFiltersForMainSearch(
        state.searchFacets,
        state.facetsThatArentLoaded,
        {
          type: state.filters.type
        },
        true /* reset */
      );

      return {
        ...state,
        searchId: state.searchId + 1,
        pageIndex: 0, // New search, reset page
        landingPagePath: null,
        filters
      };
    }

    case 'SEARCH_SET_FILTER': {
      const facet = action.key;
      const value = action.value;
      const filters = (() => {
        if (facet === 'type' && isDefined(value)) {
          const t = mainSearchTypes.find(i => i === value);
          invariant(t, 'type must be within main search types');
          return initialFacetFiltersForMainSearch(
            state.searchFacets,
            state.facetsThatArentLoaded,
            {
              type: t
            }
          );
        }
        return updateObject(state.filters, { [facet]: value });
      })();

      return {
        ...state,
        searchId: state.searchId + 1,
        pageIndex: 0, // New search, reset page
        landingPagePath: null,
        filters
      };
    }

    case 'SEARCH_DESELECT_FILTER_OPTION': {
      const facet = action.key;
      const option = action.option;
      invariant(Array.isArray(state.filters[facet]), 'Missing search filter');
      const selectedOptions = state.filters[facet].filter(v => v !== option);

      return {
        ...state,
        searchId: state.searchId + 1,
        pageIndex: 0, // New search, reset page
        landingPagePath: null,
        filters: {
          ...state.filters,
          [facet]: selectedOptions
        }
      };
    }

    case 'SEARCH_SELECT_ALL_FILTER_OPTIONS': {
      const facet = action.key;
      return {
        ...state,
        searchId: state.searchId + 1,
        pageIndex: 0, // New search, reset page
        landingPagePath: null,
        filters: {
          ...state.filters,
          [facet]: state.searchFacets[facet].options
        }
      };
    }

    case 'SET_SEARCH_FACET_OPTIONS': {
      const { options, optionTitles, facetKey, optionRankings } = action;
      const facet = state.searchFacets[facetKey];
      const facetsThatArentLoaded: Array<string> = state.facetsThatArentLoaded.filter(
        key => key !== facetKey
      );
      const sideFacets = sideFacetsForType(searchFacets, state.filters.type);
      const hasFacet = sideFacets.find(f => f.id === facet.id);
      const filterValue = facet.id && state.filters[facet.id];
      const updatedFilters =
        facet.id && hasFacet
          ? {
              ...state.filters,
              [facet.id]: filterValue || options
            }
          : state.filters;

      return {
        ...state,
        // This needs to get bumped if we change filter values
        // But if we dont, then it doesnt need to get changed
        searchId:
          filterValue && filterValue !== options
            ? state.searchId + 1
            : state.searchId,
        facetsThatArentLoaded,
        searchFacets: {
          ...state.searchFacets,
          [facetKey]: {
            ...facet,
            options,
            optionTitles,
            optionRankings
          }
        },
        // Only set the filter value if the current filter.type supports this
        // facet
        filters: updatedFilters
      };
    }

    case 'SEARCH_INITIATED_BY_USER': {
      return {
        ...state,
        searchMode: action.searchMode
      };
    }

    case 'SEARCH_STARTED': {
      return {
        ...state,
        initialSearchStarted: true,
        isSearching: true,
        queryID: null
      };
    }

    case 'SEARCH_RESULTS_SUCCESS': {
      // If the results are from an older search, ignore them
      const lastResults =
        state.resultsByPage[state.lastLoadedPageIndex.toString()];
      if (lastResults && lastResults.timestamp > action.results.timestamp) {
        return state;
      }

      const newSearch = action.results.searchId !== state.resultsForSearchId;

      return {
        ...state,
        queryID: action.results.queryID,
        resultsByPage: newSearch
          ? {
              [action.results.page]: action.results
            }
          : {
              ...state.resultsByPage,
              [action.results.page]: action.results
            },
        hasOrgResults: newSearch ? action.hasOrgResults : state.hasOrgResults,
        lastLoadedPageIndex: action.results.page,
        resultsForSearchId: action.results.searchId,
        initialResultsLoaded: true,
        isSearching: false
      };
    }

    case 'SEARCH_FETCH_PAGE_SUCCESS': {
      return {
        ...state,
        resultsByPage: {
          ...state.resultsByPage,
          [action.results.page]: action.results
        },
        isSearching: false
      };
    }

    case 'SEARCH_CLEAR_RESULTS': {
      return {
        ...state,
        resultsByPage: {},
        resultsForSearchId: null
      };
    }

    case 'SEARCH_AUTOSAVE_DISABLE': {
      return {
        ...state,
        autosaveDisabled: true
      };
    }

    case 'SEARCH_UNINITIALIZE': {
      return {
        ...state,
        initialized: false,
        landingPageInitialized: false
      };
    }

    case 'SET_USE_LOCATION': {
      return {
        ...state,
        useLocation: action.useLocation
      };
    }

    default:
      // this causes a flow error if there are any unhandled actions
      (action: empty); // eslint-disable-line no-unused-expressions
      return state;
  }
}

// Utility operations on state
export const results = (state: State) =>
  state.resultsByPage[state.pageIndex.toString()];
